import { Button, ButtonVariants, CustomClaimValue, Facility, FacilityEvent, FacilityEventType, FHWAClasses, getApiUrl, getFHWAClassesDisplayName, getTargetStatusDisplayName, getWeatherTypesDisplayName, MediaRequests, PhotoMeta, SceneType, Target, TargetMediaResponse, Targets, TargetTypes, useAuth, Vector2, WeatherTypes } from "@app/shared";
import { getEventDialogTitle as getEventDialogTitleShared, getEventTriggerName, getEventValueCards, targetDescribesEvent, EventTargetJsonView } from "@app/shared/src/components/LiveView";
import { getTargetTypeDisplayName } from "@app/shared/src/models/Shared.TargetType.gen";
import { getFHWAClassDisplayName } from "@app/shared/src/models/Shared.FHWAClass.gen";
import { getConfiguredLaneName } from "@app/shared/src/models/Shared.Lane.helpers";
import { LaneLabel } from "@app/shared/src/components/LaneLabel";
import { formatInTimeZone } from "date-fns-tz";
import { useEffect, useMemo, useState } from "react";
import { toastError, toastSuccess, useFacilityCoordsHelper } from "../../../helpers";
import { useFetch, useModel, useTargetPhoto } from "../../../hooks";
import { CheckboxInput, DataInfoCard, EnumSelect, FlagEnumCheckboxInput, IconLink, Icons, ImageLightbox, LabeledText, NumericInput, PageContainer } from "../../shared";
import { HoverTooltip } from "../../shared/HoverTooltip";
import { FacilityBackgroundView } from "../../shared/LayoutDesigner/FacilityBackgroundView";
import { DeviceMapShape, TargetMapShape } from "../../shared/maps";
import { TargetVideoSection } from "./TargetVideoSection";

type TargetDetailsDialogProps = {
    onZoomChange?: (isZoomed: boolean) => any;
    facility: Facility;
    target: Target | undefined;
    event?: FacilityEvent;
    showVideoSection?: boolean;
};

// Re-export so existing dashboard callers (FacilityEvents, OrganizationEventsTable, ScreenlineMap)
// don't need their import paths updated. Implementation lives in shared/.../eventDetailsHelpers.
export const getEventDialogTitle = getEventDialogTitleShared;

export const TargetDetailsDialog: React.FC<TargetDetailsDialogProps> = ({ onZoomChange, facility, target: val, event, showVideoSection = false }) => {
    const user = useAuth();
    const [targetMediaResponse, setTargetMediaResponse] = useState<TargetMediaResponse | undefined>();
    const [requestError, setRequestError] = useState<string | undefined>();
    // Fall back to the event's device: the event always records which device detected it, whereas the
    // target may not have loaded (dwell/presence targets are routinely purged). Without this the
    // Detection Device card rendered blank on exactly those popups.
    const deviceId = val?.deviceId ?? event?.deviceId;
    const getDeviceNameForTarget = (target?: Target) =>
        facility.devices?.find((d) => d.id === (target?.deviceId ?? event?.deviceId))?.name ?? "";
    const device = facility.devices?.find((d) => d.id === deviceId);
    const zone = facility.localTimezoneId ?? Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).resolvedOptions().timeZone;
    const help = useFacilityCoordsHelper(facility);
    const [showPhotoMeta, setShowPhotoMeta] = useState(false);
    const isSuperAdmin = user.userClaims?.find(uc => uc.claims.find(c => c === CustomClaimValue.SuperAdmin));
    const [photoMetaFetched] = useFetch(() => val ? Targets.getTargetPhotoMetadata(val.externalId) : Promise.resolve(undefined), [val?.externalId]);
    const [photoMeta, metaHelpers] = useModel(PhotoMeta, { deviceId: val?.deviceId, externalTargetId: val?.id });
    const [showPhotoLightbox, setShowPhotoLightbox] = useState(false);
    const { photoUrl, hasPhoto } = useTargetPhoto(val?.externalId, val?.deviceId, facility.organizationId);

    useEffect(() => {
        if (photoMetaFetched) {
            metaHelpers.replaceModel(photoMetaFetched);
        }
    }, [photoMetaFetched]); // eslint-disable-line react-hooks/exhaustive-deps

    const getTargetMediaRequest = async () => {
        if (device?.id && val?.id && showVideoSection) {
            try {
                const targetMediaRequest = await MediaRequests.getTargetMediaRequest(device.id, val.id);
                setTargetMediaResponse(targetMediaRequest);
            } catch (error) {
                console.error("Failed to fetch target media request:", error);
            }
        }
    };

    useEffect(() => {
        if (showVideoSection) {
            getTargetMediaRequest();
        }
    }, [device?.id, val?.id, showVideoSection]); // eslint-disable-line react-hooks/exhaustive-deps

    const deviceCenter = useMemo(() => {
        if (device) {
            return help.toLatLng(new Vector2({ x: device.positionX, y: device.positionY }));
        }
    }, [device, help]);

    const handleRequestVideo = async () => {
        try {
            setRequestError(undefined);

            if (targetMediaResponse?.deviceMediaRequest?.id && device?.id) {
                await MediaRequests.deleteRequest(device.id, targetMediaResponse.deviceMediaRequest.id);
            }

            await MediaRequests.createRequestForTarget(device?.id, val?.id);
            await getTargetMediaRequest();
            toastSuccess("Created", "Video request created.");
        } catch (ex: any) {
            const errorMessage = ex.message || ex.toString();
            const cleanMessage = errorMessage.replace(/^UserError:\s*/, "").replace(/^\w+:\s*/, "");
            setRequestError(cleanMessage);
        }
    };

    const handleSavePhotoClick = async () => {
        if (val) {
            await Targets.saveTargetPhotoMetadata(val.externalId, { ...photoMeta, targetCaptureDate: val.lastUpdate } as PhotoMeta);
            toastSuccess("Saved", "Photo metadata saved successfully.");
        } else {
            toastError("Error", "No target selected to save photo metadata for.");
        }
    };

    const shouldShowVideoSection = showVideoSection && targetMediaResponse && targetMediaResponse.hasMediaRequest;

    // Presence attaches the target that changed the count, not an occupant — see targetDescribesEvent.
    const showTargetFields = targetDescribesEvent(event);

    // Facility-scoped lane lines (no deviceId) apply everywhere; device-scoped ones only to their own
    // device. Same filter the tables and the outbound payload use.
    const laneLinesForTarget = useMemo(
        () => facility.laneLines?.filter((ll) => !ll.deviceId || ll.deviceId === val?.deviceId),
        [facility.laneLines, val?.deviceId]
    );
    const laneLabel = getConfiguredLaneName(val?.lanes, laneLinesForTarget);

    return (
        <>
            <PageContainer Icon={Icons.Route} title="Target Track">
                {device && (
                    <div className="max-h-[30vh] h-[30vh]">
                        <FacilityBackgroundView
                            facility={facility}
                            targets={val ? [val] : []}
                            showLegend={false}
                            zoomed={true}
                            mapCenter={deviceCenter}
                            mapChildren={<>
                                <DeviceMapShape facility={facility} shape={device} />
                                <TargetMapShape facility={facility} target={val} />
                            </>}
                        />
                    </div>
                )}
            </PageContainer>

            {hasPhoto && (
                <PageContainer Icon={Icons.Photo} title="Target Photo" titleRight={
                    isSuperAdmin &&
                    <IconLink Icon={Icons.Edit} onClick={() => setShowPhotoMeta(val => !val)} >{showPhotoMeta ? "Hide" : "Show"} Metadata</IconLink>
                }>
                    <div className="w-full"><img src={photoUrl} className="mx-auto my-auto cursor-zoom-in" alt="Target" title="Click to enlarge" onClick={() => setShowPhotoLightbox(true)} /></div>
                    {showPhotoMeta && (
                        <>
                            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                                <LabeledText label="Scene">
                                    <EnumSelect enumType={SceneType} {...metaHelpers.bindingsFor("scene")} />
                                </LabeledText>
                                <LabeledText label="Number of Lanes">
                                    <NumericInput {...metaHelpers.bindingsFor("numLanes")} />
                                </LabeledText>
                                <LabeledText label="Needs Review">
                                    <CheckboxInput {...metaHelpers.bindingsFor("needsReview")} checked={photoMeta.needsReview} />
                                </LabeledText>
                                <LabeledText label="Weather">
                                    <FlagEnumCheckboxInput sort={true} enumType={WeatherTypes} keyFilter={(key) => key !== WeatherTypes.Unknown} nameToDisplayConverter={getWeatherTypesDisplayName} value={photoMeta.weather} onChange={(val) => metaHelpers.updateModel("weather", val)} />
                                </LabeledText>
                                <LabeledText label="Vehicle Types">
                                    <FlagEnumCheckboxInput sort={true} enumType={TargetTypes} keyFilter={(key) => key !== TargetTypes.Unknown && key !== TargetTypes.Unspecified} nameToDisplayConverter={getTargetTypeDisplayName} value={photoMeta.targetTypesInView} onChange={(val) => metaHelpers.updateModel("targetTypesInView", val)} />
                                </LabeledText>
                                <LabeledText label="FHWA Classes">
                                    <FlagEnumCheckboxInput enumType={FHWAClasses} keyFilter={(key) => key !== FHWAClasses.Unknown} nameToDisplayConverter={getFHWAClassesDisplayName} value={photoMeta.fhwaClassesInView} onChange={(val) => metaHelpers.updateModel("fhwaClassesInView", val)} />
                                </LabeledText>

                                <LabeledText label="Dewarped Image?">
                                    <CheckboxInput {...metaHelpers.bindingsFor("dewarpedImage")} checked={photoMeta.dewarpedImage} />
                                </LabeledText>
                            </div>
                            <Button onClick={handleSavePhotoClick}>Save</Button>
                        </>
                    )}
                </PageContainer>
            )}

            {shouldShowVideoSection && (
                <PageContainer Icon={Icons.Video} title="Target Video">
                    {requestError ? (
                        <div className="text-center space-y-4">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <div className="flex items-center justify-center space-x-2 mb-2">
                                    <Icons.AlertTriangle className="w-5 h-5 text-red-600" />
                                    <span className="text-red-800 font-medium">Request Failed</span>
                                </div>
                                <p className="text-red-700">{requestError}</p>
                            </div>
                            <Button variant={ButtonVariants.Primary} onClick={handleRequestVideo} className="inline-flex items-center">
                                <Icons.Video className="w-4 h-4 mr-2" />
                                Try Again
                            </Button>
                        </div>
                    ) : (
                        <TargetVideoSection targetMediaResponse={targetMediaResponse} onRequestVideo={handleRequestVideo} deviceId={device?.id} targetId={val?.id} onStatusUpdate={setTargetMediaResponse} />
                    )}
                </PageContainer>
            )}

            <div className="grid md:grid-cols-2 gap-5">
                <DataInfoCard title="Detection Device" Icon={Icons.Satellite}>
                    <span className="text-2xl break-words">{getDeviceNameForTarget(val)}</span>
                </DataInfoCard>

                {(() => {
                    const occurredAt = event?.dateCreated ?? val?.statusStarted;
                    const occurredLabel = event ? "Event Occurred At" : "Status Started";
                    return (
                        <DataInfoCard title={occurredLabel} Icon={Icons.Clock}>
                            {occurredAt ? (
                                <HoverTooltip hoverable={
                                    <span className="flex flex-col items-end leading-tight">
                                        <span className="text-2xl">{formatInTimeZone(occurredAt, zone, "h:mm:ss a z")}</span>
                                        <span className="text-base text-muted-body-text">{formatInTimeZone(occurredAt, zone, "MMM d, yyyy")}</span>
                                    </span>
                                }>
                                    <span className="text-sm">{formatInTimeZone(occurredAt, zone, "MMM d, h:mm:ss.SSS a z")}</span>
                                </HoverTooltip>
                            ) : (
                                <span className="text-2xl">None</span>
                            )}
                        </DataInfoCard>
                    );
                })()}

                {/* Hide target type for Presence events — that card shows N occupants, so a single
                    target's type isn't representative. NearMiss still shows it (the linked target
                    is one of the pair, useful context). */}
                {/* Classified type on top, FHWA class beneath it in smaller, muted text — one card
                    showing both rather than spending a second tile on a value that only ever qualifies
                    the first. Mirrors TargetDetailsPanel. */}
                {val && showTargetFields && (
                    <DataInfoCard title="Type" Icon={Icons.Truck}>
                        <span className="flex flex-col leading-tight">
                            <span className="text-2xl break-words">{val?.type ? getTargetTypeDisplayName(val.type) : ""}</span>
                            {val?.fhwaClass && (
                                <span className="text-xs text-muted-body-text break-words">{getFHWAClassDisplayName(val.fhwaClass)}</span>
                            )}
                        </span>
                    </DataInfoCard>
                )}
                {val && showTargetFields && (!event || event.eventType !== FacilityEventType.NearMiss) && (
                    <DataInfoCard title="Target Status" Icon={Icons.Parking}>
                        <span className="text-2xl break-words">{val?.status ? getTargetStatusDisplayName(val?.status) : ""}</span>
                    </DataInfoCard>
                )}
                {(() => {
                    const eventValueCards = event ? getEventValueCards(event, val) : [];
                    const triggerName = event ? getEventTriggerName(event, facility) : undefined;
                    if (eventValueCards.length > 0 || triggerName) {
                        return (
                            <>
                                {eventValueCards.map((card) => (
                                    <DataInfoCard key={card.label} title={card.label} Icon={card.icon}>
                                        <span className="text-2xl break-words">{card.value}</span>
                                    </DataInfoCard>
                                ))}
                                {triggerName && (
                                    <DataInfoCard title="Trigger" Icon={Icons.Route}>
                                        <span className="text-2xl break-words">{triggerName}</span>
                                    </DataInfoCard>
                                )}
                            </>
                        );
                    }
                    return (
                        <>
                            <DataInfoCard title="Size" Icon={Icons.Ruler}>
                                <span className="text-2xl break-words">{val?.maximumWidth?.toFixed(1)}, {val?.maximumHeight?.toFixed(1)} m</span>
                            </DataInfoCard>
                            <DataInfoCard title="Count Adjustment" Icon={Icons.StopWatch}>
                                <span className="text-xl">{val?.netFacilityCountChange}</span>
                            </DataInfoCard>
                        </>
                    );
                })()}
                {/* Lane is target-level, so it's known for every event type, not just lane-line ones —
                    the same fact the event payload now reports as laneLineName. Rendered outside the
                    block above so it adds to the cards rather than displacing Size / Count Adjustment,
                    and always shown on an event (N/A when unknown) so its absence reads as "no lane
                    recorded" rather than a missing feature. Mirrors TargetDetailsPanel. */}
                {event && showTargetFields && (
                    <DataInfoCard title="Lane" Icon={Icons.Route}>
                        <span className="text-2xl break-words">
                            <LaneLabel lanes={val?.lanes} laneLines={laneLinesForTarget} />
                        </span>
                    </DataInfoCard>
                )}
            </div>

            {/* One implementation, shared with the firmware web config's TargetDetailsPanel — see
                EventTargetJsonView. Still super-admin only here; the on-device UI shows it to
                anyone who can already reach the device. */}
            <EventTargetJsonView facility={facility} target={val} event={event} enabled={!!isSuperAdmin} />

            <ImageLightbox show={showPhotoLightbox} src={photoUrl} alt="Target" onClose={() => setShowPhotoLightbox(false)} />
        </>
    );
};
